<script setup lang="ts">
import { UiSkeleton } from "@packages/component-library";
import { IconCloseRegular } from "@packages/icon-library";
import { useRoute } from "vue-router";
const route = useRoute();
</script>

<template>
  <section class="place-loading">
    <div class="place-loading__content">
      <div class="place-loading__slider">
        <RouterLink :to="{ name: route.name }" class="overlay-button">
          <IconCloseRegular />
        </RouterLink>
        <UiSkeleton type="card" />
      </div>

      <div class="place-loading__info">
        <UiSkeleton type="text" />
        <UiSkeleton type="text" />
        <div class="place-loading__badges">
          <UiSkeleton type="card" />
          <UiSkeleton type="card" />
          <UiSkeleton type="card" />
        </div>
        <UiSkeleton type="card" />
      </div>

      <div class="place-loading__tabs">
        <UiSkeleton type="card" />
        <UiSkeleton type="card" />
        <UiSkeleton type="card" />
      </div>

      <div class="place-loading__conditions">
        <div v-for="index in 3" :key="index" class="loading-condition">
          <UiSkeleton type="avatar" />

          <div class="loading-condition__info">
            <UiSkeleton />
            <UiSkeleton type="card" />
          </div>
        </div>
      </div>
    </div>
    <div class="place-loading__actions">
      <UiSkeleton type="card" />
      <UiSkeleton type="card" />
    </div>
  </section>
</template>

<style scoped lang="scss">
.place-loading {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr auto;
  align-items: start;

  &__content {
    overflow: auto;
    overscroll-behavior: contain;
    height: 100%;
  }

  &__slider {
    position: relative;

    > .ui-skeleton {
      height: 270px;
      border-radius: 0;
    }

    > a {
      position: absolute;
      right: 32px;
      top: 24px;
      z-index: 2;

      @media (max-width: 1023px) {
        right: 24px;
        top: 16px;
      }

      @media (max-width: 767px) {
        right: 16px;
      }
    }
  }

  &__info {
    padding: calc($base-space * 6) ($base-space * 8);

    @media (max-width: 1023px) {
      padding: calc($base-space * 5) calc($base-space * 6) calc($base-space * 6);
    }

    @media (max-width: 767px) {
      padding: calc($base-space * 4) calc($base-space * 4) calc($base-space * 5);
    }

    > .ui-skeleton_type_text {
      &:nth-child(1) {
        height: 32px;
      }
      &:nth-child(2) {
        margin: calc($base-space * 2) 0 0;
        max-width: 220px;
        height: 24px;
      }
    }

    > .ui-skeleton_type_card {
      margin: calc($base-space * 4) 0 0;
      height: 110px;
    }
  }

  &__badges {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: calc($base-space * 2);
    margin: calc($base-space * 6) 0 0;

    @media (max-width: 767px) {
      margin: calc($base-space * 5) 0 0;
    }

    > .ui-skeleton {
      height: 24px;

      &:nth-child(1) {
        width: 110px;
      }

      &:nth-child(2) {
        width: 150px;
      }

      &:nth-child(3) {
        width: 180px;
      }
    }
  }

  &__tabs {
    border-bottom: 1px solid var(--color-semantic-border-normal-soft);
    padding: 0 calc($base-space * 8) calc($base-space * 2);

    @media (max-width: 1023px) {
      padding: 0 calc($base-space * 6) calc($base-space * 2);
    }

    @media (max-width: 767px) {
      padding: 0 calc($base-space * 4) calc($base-space * 2);
    }

    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: calc($base-space * 3);

    > .ui-skeleton {
      width: 50px;
      height: 24px;
    }
  }

  &__conditions {
    padding: calc($base-space * 6) ($base-space * 8);
    display: grid;
    grid-template-columns: 1fr;
    gap: calc($base-space * 6);

    @media (max-width: 1023px) {
      padding: calc($base-space * 6);
    }

    @media (max-width: 767px) {
      padding: calc($base-space * 5) ($base-space * 4);
      gap: calc($base-space * 5);
    }
  }

  &__actions {
    display: grid;
    grid-template-columns: 48px 1fr;
    align-items: center;
    gap: calc($base-space * 3);
    padding: calc($base-space * 3) calc($base-space * 8) calc($base-space * 8);

    @media (max-width: 1023px) {
      padding: calc($base-space * 3) calc($base-space * 6) calc($base-space * 6);
    }

    @media (max-width: 767px) {
      padding: calc($base-space * 3) calc($base-space * 4) calc($base-space * 4);
      display: flex;
      flex-flow: column-reverse;
      gap: calc($base-space * 2);
    }

    > .ui-skeleton {
      height: 48px;

      @media (max-width: 1023px) {
        height: 44px;
      }
    }
  }
}

.loading-condition {
  display: grid;
  grid-template-columns: 24px 1fr;
  gap: calc($base-space * 2);

  @media (max-width: 1023px) {
    grid-template-columns: 20px 1fr;
  }

  > .ui-skeleton_type_avatar {
    width: 24px;
    height: 24px;

    @media (max-width: 1023px) {
      width: 20px;
      height: 20px;
    }
  }

  &__info {
    display: flex;
    flex-flow: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: calc($base-space * 2);

    > .ui-skeleton_type_text {
      max-width: 200px;
      height: 28px;
    }

    > .ui-skeleton_type_card {
      max-width: 280px;
      height: 24px;
    }
  }
}
</style>
